import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Star, TrendingUp } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { motion } from 'framer-motion';
import { parseCondition } from '../utils/cardCondition';
import { visibleCategory } from '../utils/reportingCategory';
import { STORES } from '../utils/stores';

export const StockBadges = ({ quantities }) => (
  <div className="flex flex-wrap gap-1.5 mt-3">
    {STORES.map(({ name, id }) => {
      const qty = Math.floor(Number(quantities?.[id]) || 0);
      if (qty <= 0) return null;
      return (
        <span
          key={id}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border bg-green-50 text-green-800 border-green-200"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-green-600" />
          {name} · {qty}
        </span>
      );
    })}
  </div>
);

// viewMode 'showcase' is the home-page tile: just the picture (condition badge top-right),
// with price, name and set underneath. `theme` picks text colours for light/dark sections;
// `ratio` is 'card' (portrait trading card) or 'square' (sealed product box).
const ProductCard = ({ product, imageUrl, viewMode = 'grid', showCondition = false, theme = 'light', ratio = 'card' }) => {
  const { addToCart } = useCart();

  const onlineAvailable = product.online_available === true;
  // Prefer the Sortswift variation name; fall back to the item name for
  // legacy singles whose condition is still baked into the name.
  const condition = showCondition
    ? parseCondition(product.variation_name || product.name)
    : null;

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (!onlineAvailable) return;
    const cartProduct = {
      // Key the cart line on variation_id: a single card is one Square item
      // with a variation per condition, so several products share a name and
      // keying on name merged Near Mint and Moderately Played into one line.
      // variation_id is also what checkout sends to Square, so the cart line
      // and the thing actually charged for stay in lockstep.
      id: product.variation_id || product.id || product.name,
      name: product.name,
      variation_name: product.variation_name,
      price: product.price,
      category: product.category,
      image: imageUrl || product.image,
      set: product.set || product.description,
      variation_id: product.variation_id,
      location_id: product.location_id,
      present_at_location_ids: product.present_at_location_ids || [],
      quantities_by_location: product.quantities_by_location || {},
      // Events live in the same catalog as physical products, so they turn up
      // in search and category listings too. Carry the flag through or the
      // cart treats a tournament entry as a shippable good and charges
      // shipping on it.
      is_event: product.is_event === true,
    };
    addToCart(cartProduct);
  };

  const isCatalogItem = Boolean(product.variation_id);
  const linkTo = isCatalogItem ? `/catalog/${encodeURIComponent(product.name)}` : `/product/${product.id}`;
  const linkState = isCatalogItem ? { item: { ...product, imageUrl } } : undefined;

  if (viewMode === 'showcase') {
    const dark = theme === 'dark';
    return (
      <Link to={linkTo} state={linkState} className="block group">
        <div className={`relative overflow-hidden rounded-lg shadow-md ${ratio === 'card' ? 'aspect-[5/7] bg-gray-900' : 'aspect-square bg-white'}`}>
          {condition && (
            <span
              className={`absolute top-2 right-2 text-xs px-2 py-1 rounded-full font-bold z-10 shadow-sm ${condition.colorClasses}`}
              title={condition.label}
            >
              {condition.code}
            </span>
          )}
          {imageUrl
            ? <img src={imageUrl} alt={product.name} className={`w-full h-full group-hover:scale-105 transition-transform duration-300 ${ratio === 'card' ? 'object-cover' : 'object-contain'}`} />
            : <span className="w-full h-full flex items-center justify-center text-5xl">{product.image || '🎴'}</span>
          }
          {/* Quick Add Overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
            {onlineAvailable ? (
              <button
                onClick={handleAddToCart}
                className="bg-white text-gray-900 px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-primary-500 hover:text-white transition-colors"
              >
                <ShoppingCart className="w-4 h-4" />
                Quick Add
              </button>
            ) : (
              <span className="bg-amber-500 text-white px-3 py-1.5 rounded-lg text-sm font-semibold">In Store Only</span>
            )}
          </div>
        </div>
        <div className="mt-2 px-0.5">
          <p className={`text-lg font-bold ${dark ? 'text-sky-300' : 'text-primary-600'}`}>${product.price.toFixed(2)}</p>
          <h3 title={product.name} className={`text-sm font-medium leading-snug line-clamp-2 ${dark ? 'text-white' : 'text-gray-900'}`}>{product.name}</h3>
          {product.set && (
            <p title={product.set} className={`text-[13px] italic mt-0.5 line-clamp-1 ${dark ? 'text-sky-200' : 'text-gray-600'}`}>{product.set}</p>
          )}
        </div>
      </Link>
    );
  }

  if (viewMode === 'list') {
    return (
      <Link to={linkTo} state={linkState}>
        <motion.div
          whileHover={{ backgroundColor: '#f9fafb' }}
          className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group flex flex-row items-center gap-4 p-3"
        >
          {/* Thumbnail */}
          <div className="relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden">
            {imageUrl
              ? <img src={imageUrl} alt={product.name} className="w-full h-full object-contain" />
              : <span className="w-full h-full flex items-center justify-center text-2xl">{product.image || '🎴'}</span>
            }
            {condition && (
              <span
                className={`absolute top-0.5 right-0.5 text-[9px] font-bold px-1 py-0.5 rounded-full z-10 shadow-sm ${condition.colorClasses}`}
                title={condition.label}
              >
                {condition.code}
              </span>
            )}
          </div>

          {/* Name + Set */}
          <div className="flex-1 min-w-0">
            <h3 title={product.name} className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2 group-hover:text-primary-600 transition-colors">
              {product.name}
            </h3>
            {product.variation_name && (
              <p title={product.variation_name} className="text-xs text-gray-600 font-medium line-clamp-1">
                {product.variation_name}
              </p>
            )}
            {product.set && (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{product.set}</p>
            )}
          </div>

          {/* Price + Cart */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-base font-bold text-gray-900">
              ${product.price.toFixed(2)}
            </span>
            {onlineAvailable ? (
              <button
                onClick={handleAddToCart}
                className="p-2 rounded-lg bg-primary-50 text-primary-600 hover:bg-primary-500 hover:text-white transition-colors"
                title="Quick Add"
              >
                <ShoppingCart className="w-4 h-4" />
              </button>
            ) : (
              <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg whitespace-nowrap">
                In Store Only
              </span>
            )}
          </div>
        </motion.div>
      </Link>
    );
  }

  return (
    <Link to={linkTo} state={linkState}>
      <motion.div
        whileHover={{ y: -5 }}
        className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group h-full flex flex-col"
      >
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden bg-white flex-shrink-0">
          {product.isNew && (
            <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold z-10">
              NEW
            </span>
          )}
          {product.isTrending && (
            <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-semibold z-10 flex items-center">
              <TrendingUp className="w-3 h-3 mr-1" />
              HOT
            </span>
          )}
          {condition && !product.isTrending && (
            <span
              className={`absolute top-2 right-2 text-xs px-2 py-1 rounded-full font-bold z-10 shadow-sm ${condition.colorClasses}`}
              title={condition.label}
            >
              {condition.code}
            </span>
          )}
          {product.discount && (
            <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold z-10">
              -{product.discount}%
            </span>
          )}

          <div className="w-full h-full flex items-center justify-center text-6xl group-hover:scale-110 transition-transform duration-300">
            {imageUrl
              ? <img src={imageUrl} alt={product.name} className="w-full h-full object-contain" />
              : (product.image || '🎴')
            }
          </div>

          {/* Quick Add Overlay */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            {onlineAvailable ? (
              <button
                onClick={handleAddToCart}
                className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold transform -translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center space-x-2 hover:bg-primary-500 hover:text-white"
              >
                <ShoppingCart className="w-4 h-4" />
                <span>Quick Add</span>
              </button>
            ) : (
              <span className="bg-amber-500 text-white px-4 py-2 rounded-lg font-semibold transform -translate-y-2 group-hover:translate-y-0 transition-all duration-300 text-sm">
                In Store Only
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-grow">
          {/* Category Badge */}
          {visibleCategory(product.category) && (
            <span className="inline-block text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-1 rounded-full mb-2 w-fit">
              {product.category}
            </span>
          )}

          {/* Product Name */}
          <h3 title={product.name} className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors min-h-[3rem]">
            {product.name}
          </h3>

          {/* Variation (condition / finish / language) */}
          {product.variation_name && (
            <p title={product.variation_name} className="text-xs text-gray-600 font-medium mb-1 line-clamp-1">
              {product.variation_name}
            </p>
          )}

          {/* Set/Collection */}
          <p className="text-sm text-gray-500 mb-2 line-clamp-1 min-h-[1.5rem]">
            {product.set || ' '}
          </p>



          {/* Price */}
          <div className="flex items-center justify-between">
            <div>
              {product.originalPrice && product.originalPrice > product.price && (
                <span className="text-sm text-gray-400 line-through">
                  ${product.originalPrice.toFixed(2)}
                </span>
              )}
              <span className="text-xl font-bold text-gray-900 ml-2">
                ${product.price.toFixed(2)}
              </span>
            </div>
            {product.stock && product.stock < 10 && (
              <span className="text-xs text-red-600 font-semibold">
                Only {product.stock} left!
              </span>
            )}
          </div>

          {isCatalogItem && <StockBadges quantities={product.quantities_by_location} />}
        </div>
      </motion.div>
    </Link>
  );
};

export default ProductCard;
